import queryString from 'querystring';

import React from 'react';

import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import Alert from 'reactstrap/lib/Alert';
import Container from 'reactstrap/lib/Container';
import Input from 'reactstrap/lib/Input';
import Label from 'reactstrap/lib/Label';

import { paymentDone } from '@ttstr/api';
import { LoadingSpinner } from '@ttstr/components';

interface RouteParams {
  contractor: string; // stripe, paypal, sofort ...
}

const PaymentDone: React.FC = () => {
  const { t } = useTranslation();
  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const location = useLocation();
  const history = useHistory();
  const { contractor } = useParams<RouteParams>();

  const { orderId, xToken, token, PayerID } = React.useMemo(() => {
    const qs = queryString.parse(location.search.substring(1));
    return {
      orderId: typeof qs.order_id === 'string' ? qs.order_id : null,
      xToken: typeof qs.xtoken === 'string' ? qs.xtoken : null,
      token: typeof qs.token === 'string' ? qs.token : null,
      PayerID: typeof qs.PayerID === 'string' ? qs.PayerID : null,
    };
  }, [location.search]);

  React.useEffect(() => {
    (async () => {
      setError(false);
      if (typeof orderId !== 'string') return;
      if (typeof xToken !== 'string') return;

      try {
        setLoading(true);
        const { order_id, simple_token } = await paymentDone(contractor, orderId, xToken, {
          token,
          PayerID,
        });
        history.push(`/orders/${order_id}/xtoken/${encodeURIComponent(simple_token)}`);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [contractor, orderId, xToken, token, PayerID]);

  return (
    <article className="my-5">
      <Container className="text-center">
        <Helmet>
          <title>{t('PAYMENT.DONE.TITLE')}</title>
        </Helmet>
        <h1 className="mb-4">{t('PAYMENT.DONE.TITLE')}</h1>
        {error ? (
          <Alert color="danger" className="text-left">
            <p>{t('PAYMENT.DONE.ERROR')}</p>
            <Label htmlFor="reference-id">{t('PAYMENT.DONE.REFERENCE_ID')}</Label>
            <Input id="reference-id" type="textarea" defaultValue={orderId} />
          </Alert>
        ) : (
          <p>{t('PAYMENT.DONE.DESCRIPTION')}</p>
        )}
        {loading && <LoadingSpinner />}
      </Container>
    </article>
  );
};

export default React.memo(PaymentDone);
